import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle, type CSSProperties } from 'react'
import type { KeyboardEvent, ClipboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { RotateCw, AtSign, Globe, Image as ImageIcon, ArrowUp, Square, Loader2 } from 'lucide-react'
import { AuthenticatedImage } from '@/components/features/authenticated-image'
import { MentionAutocomplete } from './mention-autocomplete'
import {
  SlashCommandMenu,
  type SlashWorkspaceMeta,
} from './slash-command-menu'
import { InlineReferenceChip } from './inline-reference-chip'
import { Button } from './button'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import { formatImageBudgetMeter } from '@/lib/chat-image-budget'
import type { DocumentReference } from '@/components/layout/chat-pane.types'
import type { ChatImage } from '@/components/layout/chat-pane.types'
import { 
  extractPlainText,
  serializeMessagePlainText,
  getCaretPosition, 
  restoreCaretPosition, 
  renderSkillChipAsHTML,
  renderChipAsHTML,
  readChipLineRange,
} from './contenteditable-chat-input.utils'
import { useContentEditableHandlers } from './contenteditable-chat-input.handlers'
import {
  dismissReferenceChipTooltip,
  removeAllPortaledReferenceChipTooltips,
} from './contenteditable-chat-input.chip-tooltip'
import {
  clearSkillOfferPreview,
  replaceKeywordWithSkillChip,
  restoreReplacedSkillText,
  showSkillOfferPreview,
} from './contenteditable-skill-offer'

const EMPTY_STRING_ARRAY: string[] = []

export interface ContentEditableChatInputHandle {
  insertReference: (reference: DocumentReference) => void
  /** Replace composer with one doc chip plus trailing plain text (e.g. Fix in chat). */
  setComposerWithReference: (reference: DocumentReference, text: string) => void
  activateSkill: (skill: { id: string; name: string }) => void
  focus: () => void
  clear: () => void
  clearImages: () => void
  /** Replace pasted images. Used to stash and restore a draft around queue edit. */
  setImages: (images: ChatImage[]) => void
  getImages: () => ChatImage[]
  /** Visible composer text, skipping reference chips. */
  getDraftPlainText: () => string
  /** Replace the editor with plain text and sync the parent value. */
  setPlainText: (text: string) => void
  /** Plain text plus {{FN_FOLDER}}/{{FN_DOC}} markers for chips (send/history). */
  getSerializedMessageText: () => string
  editorRef: React.RefObject<HTMLDivElement | null> | null
  /** OS file drops when parent captures drop outside the editor (e.g. over messages). Returns true if image/text/audio were handled. */
  applyNativeFileDrop: (files: FileList) => boolean
  /** Append transcript (or other plain text) after existing chips without wiping them. */
  appendComposerText: (text: string) => void
}

interface ContentEditableChatInputProps {
  value: string  // Plain text value
  references: DocumentReference[]  // Current references
  onChange: (text: string) => void
  onReferencesChange: (references: DocumentReference[]) => void
  onInsertReference?: (reference: DocumentReference) => void
  onRemoveReference?: (id: string, ref?: DocumentReference) => void
  onImagesChange?: (images: ChatImage[]) => void
  /** Seed thumbs when opening edit-and-rewind (same chrome as a new compose). */
  initialImages?: ChatImage[]
  /** True while send is uploading / starting the stream. Disables the up-arrow. */
  sendBusy?: boolean
  /** Dropped .md/.txt files - import as docs and return true if handled */
  onLocalTextFilesDrop?: (files: File[]) => Promise<boolean>
  /** Dropped .m4a/.mp3/.wav - transcribe, file, and return true if handled */
  onLocalAudioFilesDrop?: (files: File[]) => Promise<boolean>
  /** Long plain-text paste - import as doc + chip; return true if handled */
  onLongTextPaste?: (text: string) => Promise<boolean>
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  /** Workspace-tinted border/glow; overrides default primary focus chrome when set. */
  accentStyle?: {
    idle: CSSProperties
    focused: CSSProperties
  } | null
  'data-testid'?: string
  'aria-label'?: string
  // Toolbar props
  showToolbar?: boolean
  onRefresh?: () => void
  onWebSearch?: () => void
  onImageUpload?: () => void
  onSend?: () => void
  onStop?: () => void  // Show stop button when provided (replaces send button)
  /** When true, hide the toolbar send (up arrow). Use with explicit Save outside (e.g. edit-and-rewind). */
  hideToolbarSendButton?: boolean
  modelBadge?: React.ReactNode
  agentBadge?: React.ReactNode
  /** Ask / Plan / Agent mode selector (first in toolbar left cluster). */
  modeSelector?: React.ReactNode
  leftAccessory?: React.ReactNode  // Custom element for left side of toolbar (e.g., + button)
  /** Workspace scope pip - stays in the widget row with mode/model/send. */
  composerExtras?: React.ReactNode
  /** Auto-tagged current document. Own row under the widget toolbar. */
  composerContextRow?: React.ReactNode
  // Context props for mention autocomplete
  openTabIds?: string[]  // Currently open document IDs
  recentlyViewedIds?: string[]  // Recently viewed document UUIDs for search boost
  currentProjectId?: string | null  // Current project context
  /** Same trees as chat scope picker (`workspaceScopePaths`); limits @ autocomplete. */
  mentionFolderPaths?: string[]
  /** Workspace slugs in chat scope picker; limits `/` skill menu (skills with `requiredWorkspaces`). */
  slashAllowedWorkspaceSlugs?: string[]
  /** Org slug -> name + color for skill menu workspace badges */
  slashWorkspaceMetaBySlug?: Record<string, SlashWorkspaceMeta>
  /** Shown under "Skills" in `/` menu (active chat workspace names) */
  slashChatScopeLabel?: string
  /** Org ids for the current chat scope. Limits workspace skills to those orgs. */
  slashOrganizationIds?: string[]
  /** Click chip to open doc/folder — receives modifier keys like a normal sidebar click */
  onOpenReference?: (reference: DocumentReference, event: unknown) => void
  activeChatSkill?: { id: string; name: string } | null
  onActiveChatSkillChange?: (skill: { id: string; name: string } | null) => void
  /** Keyword offer under the draft. Accept replaces `keyword` with the slash badge. */
  skillOffer?: { id: string; name: string; keyword: string } | null
}

/**
 * ContentEditable input that supports inline document reference chips
 * Uses a hybrid approach: chips rendered as DOM elements, text managed separately
 */
export const ContentEditableChatInput = forwardRef<ContentEditableChatInputHandle, ContentEditableChatInputProps>(({
  value,
  references = [],
  onChange,
  onReferencesChange,
  onInsertReference,
  onRemoveReference,
  onImagesChange,
  initialImages = [],
  sendBusy = false,
  onLocalTextFilesDrop,
  onLocalAudioFilesDrop,
  onLongTextPaste,
  onKeyDown,
  placeholder = "Type @ to mention, or drag files here...",
  disabled = false,
  className = "",
  accentStyle = null,
  'data-testid': testId,
  'aria-label': ariaLabel = 'Chat input',
  showToolbar = true,
  onRefresh,
  onWebSearch,
  onImageUpload,
  onSend,
  onStop,
  hideToolbarSendButton = false,
  modelBadge,
  agentBadge,
  modeSelector,
  leftAccessory,
  composerExtras,
  composerContextRow,
  openTabIds,
  recentlyViewedIds,
  currentProjectId,
  mentionFolderPaths,
  slashAllowedWorkspaceSlugs,
  slashWorkspaceMetaBySlug,
  slashChatScopeLabel,
  slashOrganizationIds,
  onOpenReference,
  activeChatSkill,
  onActiveChatSkillChange,
  skillOffer = null,
}, ref) => {
  const editorRef = useRef<HTMLDivElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [offerPreview, setOfferPreview] = useState(false)
  const [pastedImages, setPastedImages] = useState<ChatImage[]>(() =>
    Array.isArray(initialImages) ? initialImages : [],
  )
  const pastedImagesRef = useRef<ChatImage[]>(Array.isArray(initialImages) ? initialImages : [])  // stable ref for synchronous reads
  const onImagesChangeRef = useRef(onImagesChange)
  useEffect(() => { onImagesChangeRef.current = onImagesChange }, [onImagesChange])
  const onLocalTextFilesDropRef = useRef(onLocalTextFilesDrop)
  useEffect(() => { onLocalTextFilesDropRef.current = onLocalTextFilesDrop }, [onLocalTextFilesDrop])
  const onLocalAudioFilesDropRef = useRef(onLocalAudioFilesDrop)
  useEffect(() => { onLocalAudioFilesDropRef.current = onLocalAudioFilesDrop }, [onLocalAudioFilesDrop])
  const onLongTextPasteRef = useRef(onLongTextPaste)
  useEffect(() => { onLongTextPasteRef.current = onLongTextPaste }, [onLongTextPaste])
  const isUpdatingRef = useRef(false)
  const lastPasteTimeRef = useRef(0) // Prevent duplicate paste events

  // @ mention autocomplete state
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionPosition, setMentionPosition] = useState<{ top: number; left: number } | null>(null)
  const mentionQueryRef = useRef('')
  /** Range covering `@query` when the mention menu is open; survives dropdown click. */
  const mentionInsertRangeRef = useRef<Range | null>(null)

  const [slashQuery, setSlashQuery] = useState('')
  const [slashPosition, setSlashPosition] = useState<{ top: number; left: number } | null>(null)

  // Extract all event handlers to custom hook
  const handlers = useContentEditableHandlers({
    editorRef,
    isUpdatingRef,
    lastPasteTimeRef,
    pastedImagesRef,
    onImagesChangeRef,
    onLocalTextFilesDropRef,
    onLocalAudioFilesDropRef,
    onLongTextPasteRef,
    mentionQueryRef,
    mentionInsertRangeRef,
    references,
    onChange,
    onReferencesChange,
    onInsertReference,
    onRemoveReference,
    onOpenReference,
    onActiveChatSkillChange,
    setMentionQuery,
    setMentionPosition,
    setSlashQuery,
    setSlashPosition,
    setPastedImages,
    disabled,
    onKeyDown,
    mentionPosition,
    slashPosition,
    openTabIds,
  })

  // Close menus on Escape — capture phase so it fires before React's event delegation
  // and before the menu components' own window listeners.
  useEffect(() => {
    if (mentionPosition === null && slashPosition === null) return

    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopPropagation()
      setMentionQuery('')
      setMentionPosition(null)
      mentionInsertRangeRef.current = null
      setSlashQuery('')
      setSlashPosition(null)
    }

    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [mentionPosition, slashPosition, setMentionQuery, setMentionPosition, setSlashQuery, setSlashPosition])

  // Close menus when the user clicks anywhere outside the editor or the portal menus
  useEffect(() => {
    if (mentionPosition === null && slashPosition === null) return

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null
      if (!target) return
      // Keep open if click is inside the editor
      if (editorRef.current?.contains(target)) return
      // Keep open if click is inside one of the portaled menus (they mount directly on body)
      const menuEls = document.querySelectorAll('[data-autocomplete-menu]')
      for (const el of menuEls) {
        if (el.contains(target)) return
      }
      setMentionQuery('')
      setMentionPosition(null)
      mentionInsertRangeRef.current = null
      setSlashQuery('')
      setSlashPosition(null)
    }

    document.addEventListener('pointerdown', handlePointerDown, { capture: true })
    return () => document.removeEventListener('pointerdown', handlePointerDown, { capture: true })
  }, [mentionPosition, slashPosition, editorRef, setMentionQuery, setMentionPosition, setSlashQuery, setSlashPosition])

  // Sync chips with references array - remove chips that are no longer in references
  useEffect(() => {
    if (!editorRef.current || isUpdatingRef.current) return

    const existingChips = editorRef.current.querySelectorAll('[data-reference-type="chip"]')
    
    // Remove chips that are no longer in the references array
    existingChips.forEach((chipElement) => {
      const chipId = chipElement.getAttribute('data-reference-id')
      if (!chipId) return
      
      // Check if this reference still exists (match by ID + line range)
      const chipContainer = chipElement.closest('[data-reference-id]') as HTMLElement
      if (!chipContainer) return
      
      const { lineStart: chipLineStart, lineEnd: chipLineEnd } = readChipLineRange(chipContainer)
      
      const stillExists = references.some(ref => {
        if (ref.id !== chipId) return false
        // If chip has line numbers, match them exactly
        if (chipLineStart !== undefined) {
          return ref.lineStart === chipLineStart && (ref.lineEnd ?? ref.lineStart) === chipLineEnd
        }
        // If chip has no line numbers, match refs with no line numbers
        return ref.lineStart === undefined && ref.lineEnd === undefined
      })
      
      if (!stillExists) {
        dismissReferenceChipTooltip(chipContainer)
        // Remove chip from DOM
        chipContainer.remove()
        // Sync text state
        const text = extractPlainText(editorRef.current!)
        onChange(text)
      }
    })
  }, [references, onChange])

  // Sync skill chip with activeChatSkill — remove chip from DOM when skill is cleared externally
  useEffect(() => {
    if (!editorRef.current) return
    if (!activeChatSkill) {
      const skillChips = editorRef.current.querySelectorAll('[data-skill-id]')
      if (skillChips.length > 0) {
        skillChips.forEach(chip => chip.remove())
        const text = extractPlainText(editorRef.current)
        onChange(text)
      }
    }
  }, [activeChatSkill, onChange])

  // Render text with inline chips
  // Note: Chips are inserted inline via insertReferenceAtCursor, handleMentionSelect, etc.
  // This effect only syncs text changes, preserving existing chips
  useEffect(() => {
    if (!editorRef.current || isUpdatingRef.current) return

    // Extract current text (without chips)
    const currentText = extractPlainText(editorRef.current)
    
    // Only update if text changed (but preserve chips)
    if (currentText !== value) {
      // Empty value means the user sent or cleared the draft. Keep a skill
      // badge; the placeholder must not cover it on blur.
      if (value === '' && references.length === 0) {
        const skillChips = editorRef.current.querySelectorAll('[data-skill-id]')
        if (skillChips.length === 0 && !activeChatSkill) {
          isUpdatingRef.current = true
          removeAllPortaledReferenceChipTooltips()
          editorRef.current.textContent = ''
          isUpdatingRef.current = false
        }
        return
      }
      
      // Check if we have chips - if so, don't clear them, just update text nodes
      const existingChips = editorRef.current.querySelectorAll('[data-reference-type="chip"]')
      
      if (existingChips.length > 0) {
        // We have chips - preserve them; text-only updates happen naturally via contenteditable
        return
      }
      
      // No chips yet - safe to update text
      isUpdatingRef.current = true
      const selection = window.getSelection()
      const range = selection?.rangeCount ? selection.getRangeAt(0) : null
      const cursorOffset = range ? getCaretPosition(editorRef.current, range) : null
      
      editorRef.current.textContent = value

      // Restore cursor
      if (cursorOffset !== null && value.length > 0) {
        setTimeout(() => {
          const el = editorRef.current
          if (el) {
            restoreCaretPosition(el, Math.min(cursorOffset, value.length))
          }
          isUpdatingRef.current = false
        }, 0)
      } else {
        isUpdatingRef.current = false
      }
    }
  }, [value, references.length, activeChatSkill])

  // Use handlers from custom hook
  const {
    handleInput,
    handleMentionSelect,
    handleSlashSelect,
    handleKeyDown,
    handlePaste,
    handleDrop,
    handleDragOver,
    insertReferenceAtCursor,
    applyNativeFileDrop,
  } = handlers

  // OLD HANDLERS REMOVED - now in useContentEditableHandlers hook
  /* All handlers (insertReferenceAtCursor, handleInput, handleMentionSelect, handleSlashSelect,
     handleKeyDown, parseFileReference, readImageFile, handlePaste, handleDrop, handleDragOver)
     have been moved to useContentEditableHandlers hook in contenteditable-chat-input.handlers.ts
  */

  // Keep this inline for the @ mention button - simple enough
  const handleMentionClick = () => {
    editorRef.current?.focus()
    // Insert @ symbol at cursor
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      const textNode = document.createTextNode('@')
      range.insertNode(textNode)
      range.setStartAfter(textNode)
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)
      handlers.handleInput()
    }
  }

  /* All old handler implementations (1100+ lines) removed - now in contenteditable-chat-input.handlers.ts
     The handlers used to be defined inline here as useCallback functions.
  */

  // Expose insertReference and focus via ref
  useImperativeHandle(ref, () => ({
    insertReference: insertReferenceAtCursor,
    setComposerWithReference: (reference: DocumentReference, text: string) => {
      const editor = editorRef.current
      if (!editor) return

      // Hold isUpdatingRef across the whole replace so chip-cleanup / value-sync
      // effects cannot strip the chip mid-flight and re-insert it at a stale caret
      // (that race was splicing the chip into the middle of words like quiz-*).
      isUpdatingRef.current = true
      mentionInsertRangeRef.current = null
      removeAllPortaledReferenceChipTooltips()
      editor.innerHTML = ''

      const trimmed = text.trim()
      if (trimmed) {
        // Text first, then chip at start — never rely on a leftover Selection.
        const body = document.createTextNode(`\n${trimmed}`)
        editor.appendChild(body)
      }

      const sel = window.getSelection()
      const startRange = document.createRange()
      startRange.selectNodeContents(editor)
      startRange.collapse(true)
      sel?.removeAllRanges()
      sel?.addRange(startRange)

      insertReferenceAtCursor(reference)

      // Parent refs = exactly this document (drops any prior chips).
      onReferencesChange([reference])

      const endRange = document.createRange()
      endRange.selectNodeContents(editor)
      endRange.collapse(false)
      sel?.removeAllRanges()
      sel?.addRange(endRange)

      onChange(extractPlainText(editor))
      editor.focus()

      requestAnimationFrame(() => {
        isUpdatingRef.current = false
      })
    },
    activateSkill: (skill: { id: string; name: string }) => {
      if (!editorRef.current) return
      onActiveChatSkillChange?.(skill)
      // Remove any existing skill chip
      editorRef.current.querySelectorAll('[data-skill-id]').forEach(el => el.remove())
      // Insert chip at end of content
      const chipHTML = renderSkillChipAsHTML(skill)
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = chipHTML
      const chipEl = tempDiv.firstElementChild as HTMLElement
      if (chipEl) {
        const spaceAfter = document.createTextNode('\u00A0')
        editorRef.current.appendChild(chipEl)
        editorRef.current.appendChild(spaceAfter)
        // Attach remove handler
        const removeBtn = chipEl.querySelector('[data-skill-remove]')
        if (removeBtn) {
          removeBtn.addEventListener('click', (e) => {
            e.preventDefault()
            e.stopPropagation()
            chipEl.remove()
            onActiveChatSkillChange?.(null)
            const text = extractPlainText(editorRef.current!)
            onChange(text)
            editorRef.current?.focus()
          })
        }
      }
      editorRef.current.focus()
    },
    focus: () => {
      editorRef.current?.focus()
    },
    clear: () => {
      if (!editorRef.current) return
      isUpdatingRef.current = true
      editorRef.current.textContent = ''
      isUpdatingRef.current = false
    },
    clearImages: () => {
      pastedImagesRef.current = []
      setPastedImages([])
      onImagesChangeRef.current?.([])
    },
    setImages: (images: ChatImage[]) => {
      const next = Array.isArray(images) ? images : []
      pastedImagesRef.current = next
      setPastedImages(next)
      onImagesChangeRef.current?.(next)
    },
    getImages: () => pastedImagesRef.current,
    getDraftPlainText: () =>
      editorRef.current ? extractPlainText(editorRef.current) : '',
    setPlainText: (text: string) => {
      const editor = editorRef.current
      if (!editor) return
      isUpdatingRef.current = true
      removeAllPortaledReferenceChipTooltips()
      editor.textContent = text
      onChange(text)
      requestAnimationFrame(() => {
        isUpdatingRef.current = false
      })
    },
    getSerializedMessageText: () =>
      editorRef.current ? serializeMessagePlainText(editorRef.current) : '',
    editorRef: editorRef,
    applyNativeFileDrop,
    appendComposerText: (text: string) => {
      const editor = editorRef.current
      const trimmed = text.trim()
      if (!editor || !trimmed) return
      isUpdatingRef.current = true
      const spacer = editor.textContent?.trim() ? '\n\n' : ''
      editor.appendChild(document.createTextNode(`${spacer}${trimmed}`))
      onChange(extractPlainText(editor))
      editor.focus()
      requestAnimationFrame(() => {
        isUpdatingRef.current = false
      })
    },
  }), [insertReferenceAtCursor, onActiveChatSkillChange, onChange, onReferencesChange, applyNativeFileDrop, mentionInsertRangeRef])

  const hasContent =
    value.trim().length > 0 ||
    references.length > 0 ||
    pastedImages.length > 0 ||
    Boolean(activeChatSkill)

  const visibleSkillOffer = skillOffer && slashPosition === null ? skillOffer : null

  useEffect(() => {
    if (!visibleSkillOffer) setOfferPreview(false)
  }, [visibleSkillOffer])

  useEffect(() => {
    const editor = editorRef.current
    if (!editor) return
    if (!offerPreview || !visibleSkillOffer) {
      clearSkillOfferPreview(editor)
      return
    }
    showSkillOfferPreview(editor, visibleSkillOffer.keyword)
    return () => clearSkillOfferPreview(editor)
  }, [offerPreview, visibleSkillOffer, value])

  const acceptSkillOffer = useCallback(() => {
    const editor = editorRef.current
    const offer = visibleSkillOffer
    if (!editor || !offer) return
    setOfferPreview(false)
    isUpdatingRef.current = true
    onActiveChatSkillChange?.({ id: offer.id, name: offer.name })
    editor.querySelectorAll('[data-skill-id]').forEach((el) => el.remove())
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = renderSkillChipAsHTML({ id: offer.id, name: offer.name })
    const chipEl = tempDiv.firstElementChild as HTMLElement | null
    if (!chipEl) {
      isUpdatingRef.current = false
      return
    }
    const replaced = replaceKeywordWithSkillChip(editor, offer.keyword, chipEl)
    if (!replaced) editor.appendChild(chipEl)
    const removeBtn = chipEl.querySelector('[data-skill-remove]')
    if (removeBtn) {
      removeBtn.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        restoreReplacedSkillText(chipEl)
        onActiveChatSkillChange?.(null)
        onChange(extractPlainText(editor))
        editor.focus()
      })
    }
    onChange(extractPlainText(editor))
    editor.focus()
    requestAnimationFrame(() => {
      isUpdatingRef.current = false
    })
  }, [visibleSkillOffer, onActiveChatSkillChange, onChange])

  return (
    <div className={cn("relative", className)}>
      <div
        className={cn(
          "@container/compose relative rounded-lg border bg-background shadow-sm transition-all duration-300",
          !accentStyle &&
            (isFocused
              ? "border-primary/60 shadow-[0_0_0_1px_hsl(var(--primary)/0.15),-4px_0_16px_-4px_hsl(var(--primary)/0.18)]"
              : "border-border hover:border-primary/30 hover:shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.08)]"),
          accentStyle && !isFocused && "border-border",
        )}
        style={accentStyle ? (isFocused ? accentStyle.focused : accentStyle.idle) : undefined}
      >
        {/* Editable area */}
        <div
          ref={editorRef}
          contentEditable={!disabled}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onMouseLeave={() => removeAllPortaledReferenceChipTooltips()}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className={cn(
            // pre-wrap: pasted markdown keeps line breaks in the draft (send still renders MD).
            "max-h-48 min-h-[52px] overflow-y-auto px-4 py-3.5 text-[15px] text-foreground outline-none",
            "whitespace-pre-wrap [overflow-wrap:anywhere]",
            "disabled:cursor-not-allowed disabled:opacity-50",
            isFocused && "ring-0"
          )}
          role="textbox"
          aria-label={ariaLabel}
          aria-multiline="true"
          data-testid={testId}
          suppressContentEditableWarning
        />

        {/* Placeholder - only show when empty and not focused */}
        {!hasContent && !isFocused && (
          <div className="absolute left-3 top-3.5 text-[15px] text-muted-foreground pointer-events-none select-none @[320px]/compose:left-4">
            <span className="@[360px]/compose:hidden">Message...</span>
            <span className="hidden @[360px]/compose:inline">{placeholder}</span>
          </div>
        )}

        {/* Pasted image thumbnails */}
        {pastedImages.length > 0 && (
          <div className="px-3 pt-2 pb-1">
            <p
              className="mb-1.5 text-xs text-muted-foreground"
              data-testid={testIds.chatPane.imageBudgetMeter}
            >
              {formatImageBudgetMeter(pastedImages)}
            </p>
            <div className="flex flex-wrap gap-2">
            {pastedImages.map(img => (
              <div key={img.id} className="relative group">
                <AuthenticatedImage
                  rawSrc={img.dataUrl}
                  alt="Attached image"
                  className="h-16 w-16 object-cover rounded-md border border-border"
                />
                <button
                  type="button"
                  onClick={() => {
                    const updated = pastedImages.filter(i => i.id !== img.id)
                    pastedImagesRef.current = updated
                    setPastedImages(updated)
                    onImagesChangeRef.current?.(updated)
                  }}
                  className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-foreground text-background flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-[10px] leading-none"
                  aria-label="Remove image"
                >
                  ✕
                </button>
              </div>
            ))}
            </div>
          </div>
        )}

        {visibleSkillOffer ? (
          <div className="px-4 pb-1">
            <button
              type="button"
              className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              data-testid={testIds.chatPane.skillOffer}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setOfferPreview(true)}
              onMouseLeave={() => setOfferPreview(false)}
              onFocus={() => setOfferPreview(true)}
              onBlur={() => setOfferPreview(false)}
              onClick={acceptSkillOffer}
            >
              Use {visibleSkillOffer.name}?
            </button>
          </div>
        ) : null}

        {/* Bottom toolbar: widgets stay on one row; context chips get a row below. */}
        {showToolbar && (
          <div className="toolbar border-t border-border/50">
            <div className="flex items-center gap-x-1 px-2 py-1.5 @[320px]/compose:gap-x-2 @[320px]/compose:px-3 @[320px]/compose:py-2">
              <div className="flex min-w-0 items-center gap-1 overflow-hidden @[240px]/compose:gap-1.5">
                {modeSelector ?? null}
                {leftAccessory ?? null}
                {agentBadge ?? null}
                {modelBadge ?? null}
              </div>

              {composerExtras ? (
                <div className="flex shrink-0 items-center" data-testid={testIds.chatPane.composerScope}>
                  {composerExtras}
                </div>
              ) : null}

              <div className="ml-auto flex shrink-0 items-center gap-0.5">
                {onRefresh && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0 text-muted-foreground hover:bg-muted hover:text-foreground"
                    onClick={onRefresh}
                    title="Refresh"
                    type="button"
                  >
                    <RotateCw className="h-4 w-4" />
                  </Button>
                )}
                {onWebSearch && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0 text-muted-foreground hover:bg-muted hover:text-foreground"
                    onClick={onWebSearch}
                    title="Web Search"
                    type="button"
                  >
                    <Globe className="h-4 w-4" />
                  </Button>
                )}
                {onImageUpload && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0 text-muted-foreground hover:bg-muted hover:text-foreground"
                    onClick={onImageUpload}
                    title="Upload Image"
                    type="button"
                  >
                    <ImageIcon className="h-4 w-4" />
                  </Button>
                )}
                {onStop ? (
                  <Button
                    size="sm"
                    onClick={onStop}
                    className="ml-1 h-8 w-8 rounded-md bg-muted p-0 text-muted-foreground hover:bg-muted/80 hover:text-foreground"
                    title="Stop"
                    type="button"
                  >
                    <Square className="h-3 w-3 fill-current" />
                  </Button>
                ) : !hideToolbarSendButton ? (
                  <Button
                    size="sm"
                    onClick={onSend}
                    disabled={!hasContent || sendBusy}
                    aria-busy={sendBusy}
                    className={cn(
                      'ml-1 h-8 w-8 rounded-md bg-primary p-0 text-primary-foreground hover:bg-primary/90',
                      'transition-transform duration-150 active:scale-95',
                      'disabled:opacity-30 disabled:active:scale-100',
                      sendBusy && 'opacity-80',
                    )}
                    title={sendBusy ? 'Sending…' : 'Send'}
                    type="button"
                    data-testid={testIds.chatPane.sendButton}
                  >
                    {sendBusy ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowUp className="h-4 w-4" />
                    )}
                  </Button>
                ) : null}
              </div>
            </div>

            {composerContextRow}
          </div>
        )}
      </div>

      {/* @ Mention Autocomplete - Render via portal to escape overflow containers */}
      {/* Check mentionPosition (not mentionQuery) — query is '' when user first types '@', but position is always set when in mention mode */}
      {mentionPosition !== null && typeof document !== 'undefined' && createPortal(
        <MentionAutocomplete
          query={mentionQuery}
          position={mentionPosition}
          onSelect={handleMentionSelect}
          onClose={() => {
            setMentionQuery('')
            setMentionPosition(null)
          }}
          openTabIds={openTabIds}
          recentlyViewedIds={recentlyViewedIds}
          currentProjectId={currentProjectId}
          folderPaths={mentionFolderPaths ?? EMPTY_STRING_ARRAY}
        />,
        document.body
      )}

      {slashPosition !== null && typeof document !== 'undefined' && createPortal(
        <SlashCommandMenu
          query={slashQuery}
          position={slashPosition}
          onSelect={handleSlashSelect}
          onClose={() => {
            setSlashQuery('')
            setSlashPosition(null)
          }}
          allowedWorkspaceSlugs={slashAllowedWorkspaceSlugs ?? EMPTY_STRING_ARRAY}
          workspaceMetaBySlug={slashWorkspaceMetaBySlug}
          chatScopeLabel={slashChatScopeLabel}
          organizationIds={slashOrganizationIds}
        />,
        document.body
      )}
    </div>
  )
})

ContentEditableChatInput.displayName = 'ContentEditableChatInput'
